import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Card, Alert, Spinner, Button, InputGroup } from 'react-bootstrap';
import { FaFilter, FaSearch } from 'react-icons/fa';
import SearchViewApi from '../../api/SearchViewApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import DataApi from '../../api/DataApi';
import ResizableTable from '../common/ResizableTable';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import MultiLookupLayout from '../layout/MultiLookupLayout';
import { getOperatorsForField as getOperatorsForFieldType } from '../../utils/conditionBuilderUtils';

/**
 * @param {object} [props]
 * @param {string} [props.embedObjectName] When set (e.g. modal embed), use instead of useParams — avoids nesting a Router.
 * @param {string} [props.embedSearchViewName] Slug for /search/:object/:slug; omit for default view per object.
 * @param {string} [props.embedSearch] Query string (with or without leading `?`) for initial filters.
 */
const SearchViewLayout = ({
  embedObjectName,
  embedSearchViewName,
  embedSearch
} = {}) => {
  const params = useParams();
  const location = useLocation();
  const isEmbed = Boolean(embedObjectName);
  const objectName = isEmbed ? embedObjectName : params.objectName;
  const searchViewName = isEmbed ? embedSearchViewName : params.searchViewName;

  const querySearch = useMemo(() => {
    if (isEmbed) {
      if (embedSearch == null || embedSearch === '') return '';
      const s = String(embedSearch);
      return s.startsWith('?') ? s : `?${s}`;
    }
    return location.search || '';
  }, [isEmbed, embedSearch, location.search]);

  const [searchViewConfig, setSearchViewConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objectLabel, setObjectLabel] = useState('');
  const [allFields, setAllFields] = useState([]);
  const [fieldDataTypes, setFieldDataTypes] = useState({});
  const [fieldMetadata, setFieldMetadata] = useState({}); // Store field metadata including picklist values

  // Filter states
  const [filters, setFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState('AND');
  const [quickSearch, setQuickSearch] = useState('');

  // Table states
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedRows, setSelectedRows] = useState([]);

  const abortControllerRef = useRef(null);
  const hasAppliedQueryFiltersRef = useRef(false);
  const pendingAutoSearchRef = useRef(false);

  useEffect(() => {
    if (objectName) {
      fetchSearchViewConfig();
      fetchObjectConfig();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, searchViewName]);

  // Initialize filters for all filterable fields when config is loaded
  useEffect(() => {
    if (searchViewConfig?.filterable_fields && filters.length === 0 && Object.keys(fieldDataTypes).length > 0) {
      const initialFilters = searchViewConfig.filterable_fields.map((fieldName, index) => {
        const dataType = (fieldDataTypes[fieldName] || 'text').toLowerCase();
        // Auto-set operator based on field type
        let defaultOperator = 'equals';
        if (['text', 'phone', 'textarea', 'email', 'url'].includes(dataType)) {
          defaultOperator = 'contains';
        } else if (['boolean', 'checkbox'].includes(dataType)) {
          defaultOperator = 'equals';
        }

        return {
          id: Date.now() + index,
          field: fieldName,
          operator: defaultOperator,
          value: ['picklist', 'multipicklist', 'lookup', 'reference'].includes(dataType) ? [] : ''
        };
      });
      setFilters(initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchViewConfig, fieldDataTypes]);

  useEffect(() => {
    if (!searchViewConfig || Object.keys(fieldDataTypes).length === 0) {
      return;
    }

    if (hasAppliedQueryFiltersRef.current) {
      return;
    }

    const applyQueryFilters = async () => {
      const params = new URLSearchParams(querySearch || '');
      const filterParams = [];

      for (const [key, value] of params.entries()) {
        if (!key.startsWith('filter')) {
          continue;
        }
        filterParams.push({ key, value });
      }

      const qParamEarly = (params.get('q') || '').trim();
      if (filterParams.length === 0 && !qParamEarly) {
        hasAppliedQueryFiltersRef.current = true;
        return;
      }

      const parsedFilters = [];
      const lookupResolutions = [];

      filterParams.forEach(({ value }, index) => {
        const firstColon = value.indexOf(':');
        const secondColon = value.indexOf(':', firstColon + 1);
        if (firstColon === -1 || secondColon === -1) {
          return;
        }
        const field = value.slice(0, firstColon);
        const operator = value.slice(firstColon + 1, secondColon);
        const rawValue = value.slice(secondColon + 1);

        if (!field) {
          return;
        }

        const dataType = (fieldDataTypes[field] || 'text').toLowerCase();
        const isMultiValue = operator === 'in' || (dataType === 'multipicklist' && rawValue.includes(','));
        const parsedValue = isMultiValue
          ? rawValue.split(',').map(item => item.trim()).filter(Boolean)
          : rawValue;

        if (['lookup', 'reference'].includes(dataType)) {
          const metadata = fieldMetadata[field];
          const referenceObject = metadata?.reference_object;
          const displayFieldsRaw = metadata?.display_fields || 'name';
          const displayFields = Array.isArray(displayFieldsRaw)
            ? displayFieldsRaw
            : String(displayFieldsRaw).split(',').map(item => item.trim()).filter(Boolean);
          const ids = Array.isArray(parsedValue) ? parsedValue : (parsedValue ? [parsedValue] : []);

          parsedFilters.push({
            id: Date.now() + index,
            field,
            operator,
            value: ids.map(id => ({ id, label: id }))
          });

          if (referenceObject && ids.length > 0) {
            lookupResolutions.push({
              field,
              ids,
              referenceObject,
              displayFields
            });
          }
        } else {
          parsedFilters.push({
            id: Date.now() + index,
            field,
            operator,
            value: ['picklist', 'multipicklist'].includes(dataType) && parsedValue !== ''
              ? (Array.isArray(parsedValue) ? parsedValue : [parsedValue])
              : parsedValue
          });
        }
      });

      const baseFilters = (searchViewConfig?.filterable_fields || []).map((fieldName, index) => {
        const dataType = (fieldDataTypes[fieldName] || 'text').toLowerCase();
        let defaultOperator = 'equals';
        if (['text', 'phone', 'textarea', 'email', 'url'].includes(dataType)) {
          defaultOperator = 'contains';
        } else if (['boolean', 'checkbox'].includes(dataType)) {
          defaultOperator = 'equals';
        }

        return {
          id: Date.now() + index,
          field: fieldName,
          operator: defaultOperator,
          value: ['picklist', 'multipicklist', 'lookup', 'reference'].includes(dataType) ? [] : ''
        };
      });

      if (parsedFilters.length === 0) {
        hasAppliedQueryFiltersRef.current = true;
        return;
      }

      if (lookupResolutions.length > 0) {
        const fetchLookupLabels = async (resolution) => {
          const { referenceObject, ids, displayFields } = resolution;
          try {
            const dataApi = new DataApi(`object/list/${referenceObject}`);
            const queryParams = new URLSearchParams();
            const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
            if (uniqueIds.length === 0) {
              return {};
            }
            queryParams.append('fields', ['id', ...displayFields].join(','));
            if (uniqueIds.length === 1) {
              queryParams.append('filter1', `id:equals:${uniqueIds[0]}`);
            } else {
              queryParams.append('filter1', `id:in:${uniqueIds.join(',')}`);
            }
            const response = await dataApi.fetchAll(queryParams.toString());
            if (!response?.data?.success) {
              return {};
            }
            const records = response.data.data || [];
            return records.reduce((acc, record) => {
              const recordId = record?.id?.value || record?.id || null;
              if (!recordId) {
                return acc;
              }
              const labelParts = displayFields.map(fieldName => {
                const fieldData = record[fieldName];
                if (fieldData && typeof fieldData === 'object' && !Array.isArray(fieldData)) {
                  return fieldData.displayValue || fieldData.value || '';
                }
                return fieldData || '';
              }).filter(Boolean);
              acc[recordId] = labelParts.join(' ').trim() || recordId;
              return acc;
            }, {});
          } catch (error) {
            console.warn('Failed to resolve lookup labels for', referenceObject, error);
            return {};
          }
        };

        const resolvedMaps = await Promise.all(
          lookupResolutions.map(resolution => fetchLookupLabels(resolution))
        );
        const mergedLabels = resolvedMaps.reduce((acc, map) => ({ ...acc, ...map }), {});

        parsedFilters.forEach(filter => {
          if (Array.isArray(filter.value)) {
            filter.value = filter.value.map(item => {
              if (typeof item === 'object' && item.id) {
                return {
                  ...item,
                  label: mergedLabels[item.id] || item.label || item.id
                };
              }
              return item;
            });
          }
        });
      }

      const queryLogic = params.get('logic');
      if (queryLogic === 'OR' || queryLogic === 'AND') {
        setFilterLogic(queryLogic);
      }

      const qParam = (params.get('q') || '').trim();
      if (qParam) {
        setQuickSearch(qParam);
      }

      const parsedByField = parsedFilters.reduce((acc, filter) => {
        if (filter.field) {
          acc[filter.field] = filter;
        }
        return acc;
      }, {});

      const mergedFilters = baseFilters.map((filter) => {
        const override = parsedByField[filter.field];
        if (override) {
          return {
            ...filter,
            operator: override.operator || filter.operator,
            value: override.value
          };
        }
        if (qParam && isTextSearchField(filter.field)) {
          return {
            ...filter,
            operator: 'contains',
            value: qParam,
          };
        }
        return filter;
      });

      setFilters(mergedFilters);
      pendingAutoSearchRef.current = true;
      hasAppliedQueryFiltersRef.current = true;
    };

    applyQueryFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchViewConfig, fieldDataTypes, fieldMetadata, querySearch]);

  useEffect(() => {
    if (!pendingAutoSearchRef.current) {
      return;
    }

    if (!searchViewConfig || !objectName) {
      return;
    }

    if (filters.length === 0) {
      return;
    }

    pendingAutoSearchRef.current = false;
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchViewConfig, objectName]);

  const fetchSearchViewConfig = async () => {
    try {
      const searchViewApi = new SearchViewApi();
      const response = searchViewName
        ? await searchViewApi.fetchByObjectAndSearchViewName(objectName, searchViewName)
        : await searchViewApi.fetchByObjectName(objectName);

      if (response?.data?.success) {
        const config = response.data.data;
        setSearchViewConfig(config);
        setSortBy(config.sort_by || 'createddate');
        setSortOrder(config.sort_order || 'DESC');
      } else {
        setError(`Search view not configured for ${objectName}. Please configure it in Setup.`);
      }
    } catch (err) {
      console.error('Error fetching search view config:', err);
      setError(err.response?.data?.message || 'Failed to load search view configuration');
    }
  };

  const fetchObjectConfig = async () => {
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const result = response.data;

      if (result?.object_label) {
        setObjectLabel(result.object_label);
      }

      if (result?.fields?.length > 0) {
        const dataTypes = {};
        const fields = [];
        const metadata = {};

        result.fields.forEach(field => {
          const fieldName = field.field_name;
          dataTypes[fieldName] = field.data_type || 'text';

          // Store field metadata including picklist values and lookup info
          metadata[fieldName] = {
            data_type: field.data_type || 'text',
            picklist_values: field.picklist_values_data || field.picklist_values || [],
            reference_object: field.reference_object,
            display_fields: field.display_fields,
            lookup_search_fields: field.lookup_search_fields || 'name'
          };

          // Create clean field object with only required properties
          fields.push({
            value: String(fieldName || ''),
            label: String(field.field_label || fieldName || ''),
            type: String(field.data_type || 'text'),
            is_unique: Boolean(field.is_unique || false)
          });
        });

        setFieldDataTypes(dataTypes);
        setFieldMetadata(metadata);
        setAllFields(fields);
      }
    } catch (err) {
      console.error('Error fetching object config:', err);
    }
  };

  const handleQuickSearch = () => {
    if (!quickSearch.trim()) return;

    // Find all text search fields
    const textFilters = filters.filter(f =>
      f.field && isTextSearchField(f.field)
    );

    if (textFilters.length > 0) {
      // Apply quick search to all text fields
      filters.forEach(f => {
        if (f.field && isTextSearchField(f.field)) {
          updateFilter(f.id, 'value', quickSearch.trim());
        }
      });
      // Trigger search after state updates
      setTimeout(() => {
        fetchRecords();
      }, 100);
    }
  };

  const fetchRecords = async () => {
    if (!searchViewConfig || !objectName) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError('');

    try {
      const dataApi = new DataApi(`object/list/${objectName}`);
      const queryParams = new URLSearchParams();

      // Add fields to fetch
      if (searchViewConfig.display_fields?.length > 0) {
        queryParams.append('fields', searchViewConfig.display_fields.join(','));
      }

      // Add filters
      if (filters.length > 0) {
        filters.forEach((filter, index) => {
          if (filter.field && filter.operator) {
            // Handle array values (for picklist multi-select and lookup multi-select)
            if (Array.isArray(filter.value) && filter.value.length > 0) {
              const isLookup = isLookupField(filter.field);

              if (isLookup) {
                // For lookup fields, extract IDs
                const ids = filter.value.map(val => {
                  return typeof val === 'object' && val.id !== undefined ? val.id : val;
                });

                if (ids.length > 1) {
                  // Multiple lookup values: use 'in' operator with comma-separated IDs
                  queryParams.append(`filter${index + 1}`, `${filter.field}:in:${ids.join(',')}`);
                } else if (ids.length === 1) {
                  // Single lookup value: use 'equals' operator
                  queryParams.append(`filter${index + 1}`, `${filter.field}:equals:${ids[0]}`);
                }
              } else {
                // Picklist / multipicklist: one filter with `in` so values match ANY (OR),
                // without conflicting with global AND between different filter rows.
                const picklistVals = filter.value
                  .map(v => (v == null ? '' : String(v).trim()))
                  .filter(Boolean);
                if (picklistVals.length > 1) {
                  queryParams.append(
                    `filter${index + 1}`,
                    `${filter.field}:in:${picklistVals.join(',')}`
                  );
                } else if (picklistVals.length === 1) {
                  queryParams.append(
                    `filter${index + 1}`,
                    `${filter.field}:${filter.operator}:${picklistVals[0]}`
                  );
                }
              }
            } else if (filter.value !== undefined && filter.value !== '' && !Array.isArray(filter.value)) {
              queryParams.append(`filter${index + 1}`, `${filter.field}:${filter.operator}:${filter.value}`);
            }
          }
        });
        queryParams.append('logic', filterLogic);
      }


      // Add sorting
      if (sortBy) {
        queryParams.append('sortBy', sortBy);
        queryParams.append('sortOrder', sortOrder);
      }

      const response = await dataApi.fetchAll(queryParams.toString(), {
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      if (response?.data?.success) {
        setRecords(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to fetch records');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching records:', err);
      setError(err.response?.data?.message || 'Failed to fetch records');
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const getOperatorsForField = (fieldName) => {
    const field = allFields.find(f => f.value === fieldName);
    if (!field) return [];

    const dataType = (fieldDataTypes[fieldName] || field.type || 'text').toLowerCase();
    return getOperatorsForFieldType({ data_type: dataType, type: field.type });
  };

  const isOperatorValueRequired = (operator) => {
    return !['is_null', 'is_not_null'].includes(operator);
  };


  const updateFilter = (filterId, field, value) => {
    setFilters(filters.map(f => {
      if (f.id === filterId) {
        const updated = { ...f, [field]: value };
        // If field changed, reset operator and value based on new field type
        if (field === 'field' && value) {
          const dataType = (fieldDataTypes[value] || 'text').toLowerCase();
          if (['text', 'phone', 'textarea', 'email', 'url'].includes(dataType)) {
            updated.operator = 'contains';
            updated.value = '';
          } else if (['picklist', 'multipicklist'].includes(dataType)) {
            updated.operator = 'equals';
            updated.value = [];
          } else if (['lookup', 'reference'].includes(dataType)) {
            updated.operator = 'equals';
            updated.value = [];
          } else if (['boolean', 'checkbox'].includes(dataType)) {
            updated.operator = 'equals';
            updated.value = '';
          } else {
            updated.operator = 'equals';
            updated.value = '';
          }
        }
        return updated;
      }
      return f;
    }));
  };

  const getFieldDataType = (fieldName) => {
    return (fieldDataTypes[fieldName] || 'text').toLowerCase();
  };

  const shouldShowOperator = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    return ['date', 'datetime', 'number', 'integer', 'decimal', 'currency'].includes(dataType);
  };

  const isPicklistField = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    return ['picklist', 'multipicklist'].includes(dataType);
  };

  const isTextSearchField = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    return ['text', 'phone', 'textarea', 'email', 'url'].includes(dataType);
  };

  const isCheckboxField = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    return ['boolean', 'checkbox'].includes(dataType);
  };

  const isLookupField = (fieldName) => {
    const dataType = getFieldDataType(fieldName);
    const metadata = fieldMetadata[fieldName];
    return (dataType === 'lookup' || dataType === 'reference') && metadata?.reference_object;
  };

  const prepareTableColumns = () => {
    if (!records || records.length === 0 || !searchViewConfig) return [];

    const columns = [];
    const displayFields = searchViewConfig.display_fields || [];

    displayFields.forEach((fieldName) => {
      const field = allFields.find(f => f.value === fieldName);
      if (field) {
        const dataType = fieldDataTypes[fieldName] || field.type || '';
        const isNumeric = ['currency', 'number', 'decimal', 'integer'].includes(dataType?.toLowerCase());
        columns.push({
          field: fieldName,
          label: field.label,
          sortable: true,
          dataType: dataType,
          align: isNumeric ? 'right' : 'left'
        });
      }
    });

    return columns;
  };

  const prepareTableData = () => {
    if (!records || records.length === 0) return [];

    return records.map((record) => {
      const tableRecord = { id: record.id };
      const displayFields = searchViewConfig?.display_fields || [];

      displayFields.forEach((fieldName) => {
        const fieldData = record[fieldName];

        // Handle case where fieldData is already an object (from API)
        let fieldValue, displayValue, fieldType;
        if (fieldData && typeof fieldData === 'object' && !Array.isArray(fieldData)) {
          fieldValue = fieldData.value;
          displayValue = fieldData.displayValue;
          fieldType = fieldData.type || fieldDataTypes[fieldName] || 'text';
        } else {
          fieldValue = fieldData;
          displayValue = fieldData;
          fieldType = fieldDataTypes[fieldName] || 'text';
        }

        // Check if this is a lookup/reference field
        const isLookup = isLookupField(fieldName);
        const lookupMetadata = fieldMetadata[fieldName];

        // Create clean field object with only required properties
        const fieldObject = {
          value: fieldValue,
          displayValue: displayValue,
          label: fieldName,
          type: fieldType
        };

        // Set lookup/reference properties for hyperlink rendering in ResizableTable
        if (isLookup && lookupMetadata?.reference_object) {
          fieldObject.isReference = true;
          fieldObject.referenceObject = lookupMetadata.reference_object;
          // Ensure displayValue is set (use value as fallback)
          if (!fieldObject.displayValue && fieldObject.value) {
            fieldObject.displayValue = String(fieldObject.value);
          }
        }

        // Handle UUID fields that might be lookups (fields ending with 'id')
        if (
          !isLookup &&
          (fieldType === 'uuid' || fieldName.toLowerCase().endsWith('id')) &&
          displayValue &&
          fieldValue
        ) {
          // Check if we have reference object info in metadata
          if (lookupMetadata?.reference_object) {
            fieldObject.isReference = true;
            fieldObject.referenceObject = lookupMetadata.reference_object;
          }
        }

        tableRecord[fieldName] = fieldObject;
      });

      return tableRecord;
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getRowActions = () => {
    return [
      { label: 'View', icon: 'fas fa-eye', action: 'view' },
      { label: 'Edit', icon: 'fas fa-edit', action: 'edit' },
      { label: 'Delete', icon: 'fas fa-trash', action: 'delete' }
    ];
  };

  if (!searchViewConfig && !loading) {
    return (
      <Container fluid className="mt-4">
        <Alert variant="warning">
          <h4>Search View Not Configured</h4>
          <p>This object does not have a search view configuration. Please configure it in Setup &gt; Search Views.</p>
        </Alert>
      </Container>
    );
  }

  const filterableFields = allFields.filter(f =>
    searchViewConfig?.filterable_fields?.includes(f.value)
  );

  return (
    <Container fluid className="mt-4">
      <div className="sf-header">
        <div className="sf-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="sf-title">
            <h4>{objectLabel || objectName} - Search</h4>
          </div>
          <div className="sf-actions">
            <InputGroup style={{ minWidth: '200px', maxWidth: '250px' }}>
              <Form.Control
                type="text"
                placeholder="Quick search..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && quickSearch.trim()) {
                    handleQuickSearch();
                  }
                }}
              />
              <Button
                variant="outline-secondary"
                onClick={handleQuickSearch}
              >
                <FaSearch />
              </Button>
            </InputGroup>
          </div>
        </div>
      </div>

      {/* Always Visible Filter Panel */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Filters
          </h5>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <div className="filters-container">
            <Row>
              {filters.map((filter, index) => {
                const field = filterableFields.find(f => f.value === filter.field);
                const fieldLabel = field?.label || filter.field || 'Select field...';
                const showOperator = filter.field && shouldShowOperator(filter.field);
                const isPicklist = filter.field && isPicklistField(filter.field);
                const isText = filter.field && isTextSearchField(filter.field);
                const isCheckbox = filter.field && isCheckboxField(filter.field);
                const isLookup = filter.field && isLookupField(filter.field);
                const picklistValues = filter.field ? (fieldMetadata[filter.field]?.picklist_values || []) : [];
                const lookupMetadata = filter.field ? fieldMetadata[filter.field] : null;

                return (
                  <Col key={filter.id} md={6} lg={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="mb-1 fw-bold">{fieldLabel}</Form.Label>

                        {/* Operator dropdown - only for date and number fields */}
                        {showOperator && (
                          <Form.Group className="mb-2">
                            <Form.Label className="small">Operator</Form.Label>
                            <Form.Select
                              value={filter.operator}
                              onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                              style={{
                                height: "calc(1.5em + 0.75rem + 2px)",
                                minHeight: "38px",
                                fontSize: "1rem",
                                padding: "0.375rem 0.75rem"
                              }}
                            >
                              {filter.field && getOperatorsForField(filter.field).map(op => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}

                        {/* Auto-set operator to contains for text fields (hidden) */}
                        {isText && (
                          <input
                            type="hidden"
                            value="contains"
                            onChange={() => updateFilter(filter.id, 'operator', 'contains')}
                          />
                        )}

                        {/* Auto-set operator to equals for checkbox fields (hidden) */}
                        {isCheckbox && (
                          <input
                            type="hidden"
                            value="equals"
                            onChange={() => updateFilter(filter.id, 'operator', 'equals')}
                          />
                        )}

                        {/* Multi-select for picklist fields */}
                        {isPicklist ? (
                          <MultiSelectDropdown
                              selectedValues={Array.isArray(filter.value) ? filter.value : []}
                              picklistValues={picklistValues.map((pv, idx) => {
                                // Handle different picklist value formats
                                let key, value;
                                if (typeof pv === 'object' && pv !== null) {
                                  key = pv.value_key || pv.valueKey || pv.key || pv.value || String(pv);
                                  value = pv.value_label || pv.valueLabel || pv.label || pv.value || String(pv);
                                } else {
                                  key = String(pv);
                                  value = String(pv);
                                }
                                return { key: String(key), value: String(value) };
                              })}
                              onChange={(selectedKeys) => {
                                updateFilter(filter.id, 'value', selectedKeys);
                              }}
                              placeholder="Select values..."
                            />
                        ) : isLookup ? (
                          /* Multi-select lookup for lookup/reference fields */
                          <MultiLookupLayout
                            objectName={lookupMetadata?.reference_object}
                            searchFields={lookupMetadata?.lookup_search_fields || 'name'}
                            displayFields={lookupMetadata?.display_fields || 'name'}
                            selectedValues={Array.isArray(filter.value)
                              ? filter.value.map(v => typeof v === 'object' && v.id ? v : { id: v, label: String(v) })
                              : filter.value ? [{ id: filter.value, label: String(filter.value) }] : []}
                            onChange={(selectedItems) => {
                              // Store full objects with id and label for display
                              updateFilter(filter.id, 'value', selectedItems);
                            }}
                            placeholder="Search and select..."
                          />
                        ) : isCheckbox ? (
                          /* Dropdown for checkbox/boolean fields */
                          <Form.Select
                              value={filter.value || ''}
                              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                              style={{
                                height: "calc(1.5em + 0.75rem + 2px)",
                                minHeight: "38px",
                                fontSize: "1rem",
                                padding: "0.375rem 0.75rem"
                              }}
                            >
                              <option value="">Select...</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Form.Select>
                        ) : (
                          /* Text input for other fields */
                          <Form.Control
                              type={getFieldDataType(filter.field) === 'date' || getFieldDataType(filter.field) === 'datetime' ? 'date' : 'text'}
                              value={filter.value || ''}
                              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                              disabled={!filter.operator || !isOperatorValueRequired(filter.operator)}
                              placeholder={!isOperatorValueRequired(filter.operator) ? 'Not required' : isText ? 'Search...' : 'Enter value...'}
                              style={{
                                height: "calc(1.5em + 0.75rem + 2px)",
                                minHeight: "38px",
                                fontSize: "1rem",
                                padding: "0.375rem 0.75rem"
                              }}
                            />
                        )}
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Search Button */}
          <Row className="mt-3">
            <Col className="text-end">
              <Button
                variant="primary"
                size="sm"
                onClick={fetchRecords}
              >
                <FaSearch className="me-2" />
                Search
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Results Table */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && records.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading records...</p>
        </div>
      ) : (
        <ResizableTable
          data={prepareTableData()}
          columns={prepareTableColumns()}
          loading={loading}
          error={error}
          searchTerm=""
          onSearchChange={() => {}}
          currentPage={1}
          totalRecords={records.length}
          recordsPerPage={records.length}
          onPageChange={() => {}}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          showSerialNumber={true}
          showActions={true}
          actions={getRowActions()}
          selectedRows={selectedRows}
          onRowSelectionChange={setSelectedRows}
          exportFilename={`${objectName}_search_results`}
          exportSheetName={objectName}
          className="search-resizable-table"
          tableClassName="table-theme"
          emptyMessage="No records found"
          showSearch={false}
          showPagination={false}
          objectName={objectName}
          sourcePage="search"
          onRefresh={fetchRecords}
          allObjectFields={allFields.map(f => ({
            value: String(f.value || ''),
            label: String(f.label || ''),
            type: String(f.type || 'text'),
            is_unique: Boolean(f.is_unique || false),
            is_required: false,
            data_type: String(f.type || 'text')
          }))}
          recordLinkField={searchViewConfig?.display_fields?.[0] || 'id'}
        />
      )}
    </Container>
  );
};

export default SearchViewLayout;
